import type { SelectOption } from "@msys/ui";
import { Close as CloseIcon, Error as ErrorIcon } from "@mui/icons-material";
import {
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  MenuItemProps,
  Select as MuiSelect,
  SelectProps as MuiSelectProps,
  Stack,
  Tooltip,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useField } from "formik";
import { isBoolean, isNumber, isString, isUndefined } from "lodash-es";
import React from "react";
import { Capabilities } from "../../../clients/graphqlTypes.js";
import { RestrictedByCapabilityWithDebug } from "../../auth/RestrictedByCapability.js";

export type { SelectOption };

export interface SelectFieldProps<Value> extends Omit<
  MuiSelectProps<Value>,
  "value" | "renderValue" | "multiple" | "onChange"
> {
  renderValue?: (value: Value) => React.ReactNode;
  name: string;
  options?: (SelectOption<Value> & { capability?: Capabilities })[];
  clearable?: boolean;
  onClear?: () => void;
  value?: Value | null;
  onChange?: (value: Value | null) => void | Promise<void>;
}

export const SelectField = <Value,>({
  fullWidth,
  name,
  options = [],
  clearable = false,
  onChange: onChangeProp,
  value: valueProp,
  onBlur: onBlurProp,
  onClear,
  onClose,
  label,
  disabled,
  ...props
}: SelectFieldProps<Value>) => {
  const { t } = useTranslate(["Global"]);
  const [{ value, onBlur }, { touched, error }, { setValue, setTouched }] =
    useField<Value | null>(name);

  const onChange = async (value: Value | null) => {
    if (onChangeProp) {
      await onChangeProp(value);
    } else {
      await setValue(value);
      requestAnimationFrame(() => {
        setTouched(true);
      });
    }
  };
  const currentValue = (valueProp !== undefined ? valueProp : value) ?? null;

  const labelId = `${name}-label`;

  return (
    <FormControl
      disabled={disabled}
      error={Boolean(error)}
      fullWidth={fullWidth}
      sx={{
        ...(props.size === "extra-small" && {
          "& .MuiInputLabel-root": { display: "none !important" },
        }),
      }}
    >
      <InputLabel id={labelId}>{label}</InputLabel>
      <MuiSelect
        name={name}
        value={currentValue ?? ""}
        multiple={false}
        onChange={async e => {
          const newValue = (e.target.value ?? null) as Value;
          await onChange(newValue);
        }}
        onBlur={e => {
          onBlurProp?.(e);
          onBlur(e);
        }}
        label={label}
        labelId={labelId}
        fullWidth={fullWidth}
        onClose={onClose}
        inputProps={{
          name,
          id: name,
        }}
        IconComponent={
          clearable && currentValue !== null ? EmptyIcon : undefined
        }
        endAdornment={
          clearable && currentValue !== null ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                color="primary"
                onClick={
                  onClear ??
                  (async () => {
                    await onChange(null);
                  })
                }
                aria-label="Clear"
                style={{ position: "relative", top: 8 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined
        }
        sx={{
          "&.MuiInputBase-adornedEnd > .MuiInputBase-input": {
            paddingRight: "0 !important",
          },
        }}
        // error handling for extra-small size
        {...(props.size === "extra-small" && error
          ? {
              startAdornment: (
                <Tooltip title={error}>
                  <ErrorIcon fontSize="small" color="error" />
                </Tooltip>
              ),
              error: true,
              helperText: null,
            }
          : undefined)}
        {...props}
      >
        {options.map(option => {
          const key = option.key ?? JSON.stringify(option.value);
          const field = (
            <>
              {option.icon ? (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: "center",
                  }}
                >
                  {option.icon}
                  <div>{option.label}</div>
                </Stack>
              ) : (
                option.label
              )}
            </>
          );
          if (option.capability) {
            return (
              <RestrictedByCapabilityWithDebugMenuItem
                key={key}
                capability={option.capability}
                // @ts-ignore MenuItem only allows number | string | readonly string[] https://github.com/mui-org/material-ui/issues/14286
                value={option.value}
                style={{ minHeight: "36px" }}
              >
                {field}
              </RestrictedByCapabilityWithDebugMenuItem>
            );
          }
          return (
            <MenuItem
              key={key}
              // @ts-ignore MenuItem only allows number | string | readonly string[] https://github.com/mui-org/material-ui/issues/14286
              value={option.value}
              style={{ minHeight: "36px" }}
            >
              {field}
            </MenuItem>
          );
        })}
        {!options || !options.length ? (
          <MenuItem disabled style={{ minHeight: "36px" }}>
            {t("No options")}
          </MenuItem>
        ) : null}
      </MuiSelect>
      {touched && error && isString(error) ? (
        <FormHelperText>{error}</FormHelperText>
      ) : null}
    </FormControl>
  );
};
const EmptyIcon = () => <></>;
const RestrictedByCapabilityWithDebugMenuItem = ({
  capability,
  children,
  ...props
}: React.PropsWithChildren<
  {
    capability: React.ComponentProps<
      typeof RestrictedByCapabilityWithDebug
    >["capability"];
  } & MenuItemProps
>) => (
  <RestrictedByCapabilityWithDebug capability={capability}>
    <MenuItem {...props}>{children}</MenuItem>
  </RestrictedByCapabilityWithDebug>
);

export const SelectNumberField = (props: SelectFieldProps<number>) => {
  const [, , { setValue, setTouched }] = useField<number | null>(props.name);

  return (
    <SelectField
      {...props}
      // we must use `onClose` instead of `onChange` to be able to trigger validation when users click outside of the select list.
      onClose={async (e: React.SyntheticEvent) => {
        const dataset = (e.target as any).dataset as DOMStringMap;
        if (dataset && !isUndefined(dataset.value)) {
          // out-of-sync issue since November 2019: https://github.com/formium/formik/issues/2059#issuecomment-890613538
          // without the await, formik validates with the former value
          const value = isString(dataset.value)
            ? dataset.value
              ? parseFloat(dataset.value)
              : null
            : isNumber(dataset.value)
              ? dataset.value
              : null;
          await setValue(value, false);
        }
        await setTouched(true, true);
      }}
      onClear={() => setValue(null)}
    />
  );
};

export const SelectBooleanField = (props: SelectFieldProps<boolean>) => {
  const [, , { setValue, setTouched }] = useField<boolean | null>(props.name);

  return (
    <SelectField
      {...props}
      onClose={async (e: React.SyntheticEvent) => {
        const dataset = (e.target as any).dataset as DOMStringMap;
        if (dataset && !isUndefined(dataset.value)) {
          // out-of-sync issue since November 2019: https://github.com/formium/formik/issues/2059#issuecomment-890613538
          // without the await, formik validates with the former value
          const value = isBoolean(dataset.value)
            ? dataset.value
            : dataset.value === "true"
              ? true
              : dataset.value === "false"
                ? false
                : null;
          await setValue(value, false);
        }
        await setTouched(true, true);
      }}
      onClear={() => setValue(null)}
    />
  );
};
